import { useRef, useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'

import { useDispatch } from 'react-redux'
import { setCredentials } from './authSlice'
import { useLoginMutation } from './authApiSlice'

import usePersist from '@/hooks/usePersist'
import useTitle from '@/hooks/useTitle'

import { useUpdateUserPasswordMutation } from '@/pages/admin/users/usersApiSlice'

// 16 Jul 2026
// Trinity logo
import EtEtTrinityIcon6SVGComp from '@/assets/img/icons/svgComp/EtEtTrinityIcon6SVGComp'


// 0a - Rotating Trinity Messages - 16 Jul 2026
const trinityMessages = [
  {
    eyebrow: 'Trinity',
    title: 'Activate & Amplify Growth.',
  },
  {
    eyebrow: 'Understand',
    title: 'See what truly matters.',
  },
  {
    eyebrow: 'Align',
    title: 'Bring purpose into focus.',
  },
  {
    eyebrow: 'Activate',
    title: 'Move insight into action.',
  },
  {
    eyebrow: 'Amplify',
    title: 'Your purpose native journey',
  },
]


// 0b - Password Field Component - 17 Jul 2026
const PasswordField = ({
  id,
  label,
  value,
  onChange,
  visible,
  onToggle,
  autoComplete,
  isDark,
}) => {
  const inputClass = isDark
    ? 'border-gray-700 text-white placeholder:text-gray-600 hover:border-gray-600 focus:border-orange-500'
    : 'border-gray-300 text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:border-orange-500'

  const visibilityButtonClass = isDark
    ? 'text-gray-500 hover:bg-white/[0.06] hover:text-gray-200'
    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'

  return (
    <div className="group relative">
      <LockIcon isDark={isDark} />

      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={label}
        aria-label={label}
        autoComplete={autoComplete}
        required
        className={`w-full border-0 border-b bg-transparent py-3 pl-9 pr-12 text-sm outline-none transition focus:ring-0 ${inputClass}`}
      />

      <button
        type="button"
        onClick={onToggle}
        aria-label={
          visible
            ? `Hide ${label.toLowerCase()}`
            : `Show ${label.toLowerCase()}`
        }
        className={`absolute right-0 top-1/2 -translate-y-1/2 rounded-full p-2 transition focus:outline-none ${visibilityButtonClass}`}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  )
}


// 0c - Username Icon - 17 Jul 2026
const UserIcon = ({ isDark }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className={`absolute left-0 top-1/2 h-5 w-5 -translate-y-1/2 transition group-focus-within:text-orange-500 ${
      isDark ? 'text-gray-600' : 'text-gray-400'
    }`}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6.75a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0ZM4.5 20.1a7.5 7.5 0 0115 0"
    />
  </svg>
)


// 0d - Password Icon - 17 Jul 2026
const LockIcon = ({ isDark }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className={`absolute left-0 top-1/2 h-5 w-5 -translate-y-1/2 transition group-focus-within:text-orange-500 ${
      isDark ? 'text-gray-600' : 'text-gray-400'
    }`}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7.5 10.5V8.25a4.5 4.5 0 019 0v2.25M6.75 10.5h10.5A1.75 1.75 0 0119 12.25v7A1.75 1.75 0 0117.25 21H6.75A1.75 1.75 0 015 19.25v-7a1.75 1.75 0 011.75-1.75Z"
    />
  </svg>
)


// 0e - Show Password Icon - 17 Jul 2026
const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12s3.5-6 9.75-6 9.75 6 9.75 6-3.5 6-9.75 6-9.75-6-9.75-6Z"
    />

    <circle
      cx="12"
      cy="12"
      r="2.75"
    />
  </svg>
)


// 0f - Hide Password Icon - 17 Jul 2026
const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m3 3 18 18M10.6 10.7A2.8 2.8 0 0012 15a2.8 2.8 0 002-4.8M9.9 5.3A10.7 10.7 0 0112 5c6.25 0 9.75 7 9.75 7a17 17 0 01-2.4 3.25M6.4 6.4C3.7 8.3 2.25 12 2.25 12s3.5 7 9.75 7a10.6 10.6 0 004.15-.85"
    />
  </svg>
)


// 0g - Button Arrow Icon - 17 Jul 2026
const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 12h14m-5-5 5 5-5 5"
    />
  </svg>
)


// 0h - Light Theme Icon - 17 Jul 2026
const SunIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-4 w-4"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="3.5"
    />

    <path
      strokeLinecap="round"
      d="M12 2.5v2M12 19.5v2M4.5 12h-2M21.5 12h-2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4"
    />
  </svg>
)


// 0i - Dark Theme Icon - 17 Jul 2026
const MoonIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-4 w-4"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20.2 15.2A8.2 8.2 0 018.8 3.8 8.7 8.7 0 1020.2 15.2Z"
    />
  </svg>
)


const Login = () => {
  // Author - Atul Pandey
  // Updated - 19 Feb 2025 - changed navigation to home page based on screen size
  // Updated - 19 Feb 2025 - changed JSX for small screens
  // Updated - 02 Mar 2025 - temp password check
  // Updated - 08 Dec 2025 - userTimezone population
  // Updated - 16 Jul 2026 - refined visual appeal
  // Updated - 17 Jul 2026 - further visual refinements
  // Updated - 17 Jul 2026 - added light and dark theme toggle
  // Updated - 17 Jul 2026 - simplified to a centered login dialog
  // Updated - 17 Jul 2026 - moved rotating message into login dialog and reduced page height
  // Updated - 17 Jul 2026 - widened login dialog and separated logo, message and form sections
  // Updated - 17 Jul 2026 - consolidated Trinity identity into rotating messages
  // Updated - 17 Jul 2026 - removed visible username and password labels

  // 0 - Use Title
  useTitle('Et - Login')

  // 1a - State Variables
  const userRef = useRef()
  const errRef = useRef()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [errMsg, setErrMsg] = useState('')
  const [persist, setPersist] = usePersist()

  // 1b - State variables for password reset if current password is temp - 02 Mar 2025
  const [isTempPassword, setIsTempPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetError, setResetError] = useState('')

  // 1c - Password Visibility States - 16 Jul 2026
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // 1d - Rotating Message State - 16 Jul 2026
  const [activeMessageIndex, setActiveMessageIndex] = useState(0)

  // 1e - Theme State - 17 Jul 2026
  // Light theme remains the default each time the login page opens.
  const [isDark, setIsDark] = useState(false)

  // 2a - Navigation & Redux
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // 2b - Login and Password Reset Mutations
  const [login, { isLoading }] = useLoginMutation()

  const [updateUserPassword] =
    useUpdateUserPasswordMutation() // 02 Mar 2025

  // 2c - Theme Based Class Variables - 17 Jul 2026
  const pageClass = isDark
    ? 'bg-[#0d0d0e] text-white'
    : 'bg-gradient-to-b from-white via-gray-50 to-orange-50/60 text-gray-900'

  const mainTextClass = isDark
    ? 'text-white'
    : 'text-gray-900'

  const mutedTextClass = isDark
    ? 'text-gray-500'
    : 'text-gray-500'

  const secondaryTextClass = isDark
    ? 'text-gray-600'
    : 'text-gray-400'

  const dialogClass = isDark
    ? 'border-white/[0.08] bg-[#141415]/90 shadow-black/40'
    : 'border-gray-200/80 bg-white/90 shadow-gray-300/50'

  const usernameInputClass = isDark
    ? 'border-gray-700 text-white placeholder:text-gray-600 hover:border-gray-600 focus:border-orange-500'
    : 'border-gray-300 text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:border-orange-500'

  const trustLabelClass = isDark
    ? 'text-gray-400'
    : 'text-gray-500'

  const dividerClass = isDark
    ? 'border-gray-800'
    : 'border-gray-200'

  const messageDividerClass = isDark
    ? 'border-gray-800'
    : 'border-gray-200'

  const themeToggleContainerClass = isDark
    ? 'border-white/10 bg-white/[0.05]'
    : 'border-gray-200 bg-white/75'

  // 2d - Focus Username on Page Load
  useEffect(() => {
    userRef.current?.focus()
  }, [])

  // 2e - Clear Login Error when Credentials Change
  useEffect(() => {
    setErrMsg('')
  }, [username, password])

  // 2f - Rotate Trinity Messages - 16 Jul 2026
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setActiveMessageIndex(
        (currentIndex) =>
          (currentIndex + 1) % trinityMessages.length
      )
    }, 3000)

    return () => clearInterval(messageInterval)
  }, [])

  // 2g - Focus Login Error for Accessibility
  useEffect(() => {
    if (errMsg) {
      errRef.current?.focus()
    }
  }, [errMsg])

  // 3 - HandleSubmit
  const handleSubmit = async (
    e,
    overridePassword = null
  ) => {
    e.preventDefault()
    setErrMsg('') // 02 Mar 2025

    try {
      // const { accessToken } = await login({ username, password }).unwrap() // Old code - muted 02 Mar 2025

      // const response = await login({ username, password }).unwrap() // 02 Mar 2025

      // 08 Dec 2025 — Detect user's timezone for auto-populate
      const userTimeZone =
        Intl.DateTimeFormat().resolvedOptions().timeZone

      const response = await login({
        username,
        password: overridePassword || password,
        userTimeZone: userTimeZone, // ⭐ 08 Dec 2025 NEW FIELD SENT TO BACKEND
      }).unwrap() // ✅ Ensure the correct field name

      // Step 1: Check if the response indicates a temp password
      if (response.requiresPasswordReset) {
        setIsTempPassword(true)
        return
      }

      // Step 2: Proceed with normal login
      // dispatch(setCredentials({ accessToken }))
      dispatch(
        setCredentials({
          accessToken: response.accessToken,
        })
      )

      setUsername('')
      setPassword('')

      // Detect screen size -- 19 Feb 2025
      const screenWidth = window.innerWidth

      if (screenWidth < 768) {
        // For small screens (sm/xs)
        navigate('/etapp/mobile')
      } else {
        // For medium screens and larger (md+)
        navigate('/etapp')
      }

      // navigate('/etapp')
    } catch (err) {
      const errorMessage =
        err?.data?.message ||
        'An unexpected error occurred'

      setErrMsg(errorMessage) // Update errMsg based on server response or a fallback error message
    }
  }

  // 4a - Input Functions
  const handleUserInput = (e) =>
    setUsername(e.target.value)

  const handlePwdInput = (e) =>
    setPassword(e.target.value)

  // 4b - Persist Device Function
  const handleToggle = () =>
    setPersist((prev) => !prev)

  // 4c - Theme Toggle Function - 17 Jul 2026
  const handleThemeToggle = () =>
    setIsDark((prev) => !prev)

  // 5 - Handle Password Reset for temp - 02 Mar 2025
  const handlePasswordReset = async (e) => {
    e.preventDefault()
    setResetError('')

    // Step 1: Validate that the new password and confirmation match
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.')
      return
    }

    try {
      // Step 2: Call the API to update the password
      await updateUserPassword({
        username,
        newPassword,
      }).unwrap()

      // Step 3: Reset temp password flag and log in again
      setIsTempPassword(false)
      setPassword(newPassword)

      // handleSubmit(e);
      await handleSubmit(e, newPassword)
    } catch (err) {
      setResetError(
        err?.data?.message ||
        'Error updating password'
      )
    }
  }

  // 6 - Loading Screen - 16 Jul 2026
  if (isLoading) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center font-poppins transition-colors duration-500 ${
          isDark
            ? 'bg-[#0d0d0e]'
            : 'bg-gradient-to-b from-white via-gray-50 to-orange-50/60'
        }`}
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className={`h-10 w-10 animate-spin rounded-full border-2 border-t-orange-500 ${
              isDark
                ? 'border-white/15'
                : 'border-gray-200'
            }`}
          />

          <p
            className={`text-sm ${
              isDark
                ? 'text-gray-400'
                : 'text-gray-500'
            }`}
          >
            Opening Trinity...
          </p>
        </div>
      </div>
    )
  }

  // 7 - Main Page Content
  const content = (
    <main
      className={`relative flex min-h-screen flex-col overflow-hidden font-poppins transition-colors duration-500 ${pageClass}`}
    >
      {/* 7a - Minimal Background Illumination */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div
          className={`absolute -left-52 -top-52 h-[500px] w-[500px] rounded-full blur-[150px] ${
            isDark
              ? 'bg-orange-600/10'
              : 'bg-orange-200/35'
          }`}
        />

        <div
          className={`absolute -bottom-64 right-[-170px] h-[580px] w-[580px] rounded-full blur-[165px] ${
            isDark
              ? 'bg-orange-900/10'
              : 'bg-orange-100/50'
          }`}
        />
      </div>

      {/* 7b - Theme Toggle */}
      <div className="absolute right-4 top-4 z-30 sm:right-7 sm:top-6">
        <button
          type="button"
          onClick={handleThemeToggle}
          aria-label={
            isDark
              ? 'Switch to light theme'
              : 'Switch to dark theme'
          }
          title={
            isDark
              ? 'Switch to light theme'
              : 'Switch to dark theme'
          }
          className={`flex h-9 items-center gap-1 rounded-full border p-1 shadow-sm backdrop-blur-md transition-colors duration-300 ${themeToggleContainerClass}`}
        >
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full transition-all duration-300 ${
              !isDark
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-gray-500'
            }`}
          >
            <SunIcon />
          </span>

          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full transition-all duration-300 ${
              isDark
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-gray-400'
            }`}
          >
            <MoonIcon />
          </span>
        </button>
      </div>

      {/* 8 - Centered Page Layout */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-6 sm:px-6 sm:py-8">

        {/* 9 - Login Dialog */}
        <section
          className={`w-full max-w-[560px] rounded-[28px] border px-7 py-7 shadow-xl backdrop-blur-xl transition-colors duration-500 sm:px-10 sm:py-8 ${dialogClass}`}
        >
          {/* 9a - Trinity Logo and Rotating Message */}
          <div className="flex items-center gap-5 sm:gap-8">

            {/* 9a1 - Trinity Logo */}
            <Link
              to="/"
              className="shrink-0"
              aria-label="Go to Trinity home"
            >
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-full bg-black shadow-lg sm:h-16 sm:w-16 ${
                  isDark
                    ? 'shadow-black/50 ring-1 ring-orange-500/20'
                    : 'shadow-gray-400/50'
                }`}
              >
                <EtEtTrinityIcon6SVGComp className="h-10 w-10 text-orange-500 sm:h-12 sm:w-12" />
              </div>
            </Link>

            {/* 9a2 - Rotating Trinity Message */}
            <div className="min-w-0 flex-1">
              <div
                key={activeMessageIndex}
                className="animate-[fadeMessage_700ms_ease-out]"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-orange-600">
                  {
                    trinityMessages[
                      activeMessageIndex
                    ].eyebrow
                  }
                </p>

                <h1
                  className={`mt-1.5 text-xl font-semibold leading-6 tracking-[-0.025em] sm:text-xl sm:leading-7 ${mainTextClass}`}
                >
                  {
                    trinityMessages[
                      activeMessageIndex
                    ].title
                  }
                </h1>
              </div>

              {/* 9a3 - Rotating Message Indicators */}
              <div className="mt-3 flex items-center gap-2">
                {trinityMessages.map(
                  (message, index) => (
                    <button
                      key={message.eyebrow}
                      type="button"
                      onClick={() =>
                        setActiveMessageIndex(index)
                      }
                      aria-label={`Show ${message.eyebrow} message`}
                      className={`h-1 rounded-full transition-all duration-500 ${
                        index === activeMessageIndex
                          ? 'w-7 bg-orange-500'
                          : isDark
                            ? 'w-3 bg-gray-700 hover:bg-gray-600'
                            : 'w-3 bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  )
                )}
              </div>
            </div>
          </div>

          {/* 9b - Header Divider */}
          <div
            className={`my-6 border-t ${messageDividerClass}`}
          />

          {/* 9c - Temporary Password Prompt */}
          {isTempPassword && (
            <div className="mb-5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.28em] text-orange-600">
                Secure access
              </p>

              <p
                className={`mt-1.5 text-sm leading-6 ${mutedTextClass}`}
              >
                Choose a secure password before continuing.
              </p>
            </div>
          )}

          {/* 9d - Login Error */}
          {errMsg && (
            <div
              ref={errRef}
              tabIndex="-1"
              aria-live="assertive"
              className={`mb-5 border-l-2 border-red-500 px-4 py-3 text-sm leading-5 outline-none ${
                isDark
                  ? 'bg-red-500/10 text-red-300'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {errMsg}
            </div>
          )}

          {/* 10 - Temporary Password Reset Form */}
          {isTempPassword ? (
            <form
              onSubmit={handlePasswordReset}
              className="space-y-5"
            >
              {/* 10a - New Password */}
              <PasswordField
                id="newPassword"
                label="New password"
                value={newPassword}
                onChange={(e) =>
                  setNewPassword(e.target.value)
                }
                visible={showNewPassword}
                onToggle={() =>
                  setShowNewPassword(
                    (prev) => !prev
                  )
                }
                autoComplete="new-password"
                isDark={isDark}
              />

              {/* 10b - Confirm Password */}
              <PasswordField
                id="confirmPassword"
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) =>
                  setConfirmPassword(
                    e.target.value
                  )
                }
                visible={showConfirmPassword}
                onToggle={() =>
                  setShowConfirmPassword(
                    (prev) => !prev
                  )
                }
                autoComplete="new-password"
                isDark={isDark}
              />

              {/* 10c - Password Reset Error */}
              {resetError && (
                <p
                  className={`border-l-2 border-red-500 px-4 py-3 text-sm ${
                    isDark
                      ? 'bg-red-500/10 text-red-300'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {resetError}
                </p>
              )}

              {/* 10d - Update Password Button */}
              <button
                type="submit"
                disabled={
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword
                }
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-700 via-orange-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-orange-500/25 focus:outline-none focus:ring-4 focus:ring-orange-300/40 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Update password
                <ArrowIcon />
              </button>
            </form>
          ) : (
            /* 11 - Standard Login Form */
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* 11a - Username */}
              <div className="group relative">
                <UserIcon isDark={isDark} />

                <input
                  id="username"
                  ref={userRef}
                  type="text"
                  value={username}
                  onChange={handleUserInput}
                  placeholder="Username"
                  aria-label="Username"
                  autoComplete="username"
                  required
                  className={`w-full border-0 border-b bg-transparent py-3 pl-9 pr-4 text-sm outline-none transition focus:ring-0 ${usernameInputClass}`}
                />
              </div>

              {/* 11b - Password */}
              <PasswordField
                id="password"
                label="Password"
                value={password}
                onChange={handlePwdInput}
                visible={showPassword}
                onToggle={() =>
                  setShowPassword(
                    (prev) => !prev
                  )
                }
                autoComplete="current-password"
                isDark={isDark}
              />

              {/* 11c - Device Trust and Forgot Password */}
              <div className="flex items-center justify-between gap-4">
                <label
                  htmlFor="persist"
                  className={`flex cursor-pointer items-center gap-2.5 text-xs ${trustLabelClass}`}
                >
                  <input
                    id="persist"
                    type="checkbox"
                    checked={persist}
                    onChange={handleToggle}
                    className={`h-4 w-4 rounded text-orange-600 focus:ring-orange-500/30 ${
                      isDark
                        ? 'border-gray-700 bg-transparent'
                        : 'border-gray-300 bg-white'
                    }`}
                  />

                  Trust this device
                </label>

                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-orange-600 transition hover:text-orange-700"
                >
                  Forgot password?
                </Link>
              </div>

              {/* 11d - Login Button */}
              <button
                type="submit"
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-700 via-orange-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-orange-500/25 focus:outline-none focus:ring-4 focus:ring-orange-300/40"
              >
                Enter Trinity
                <ArrowIcon />
              </button>
            </form>
          )}

          {/* 12 - Copyright */}
          <div
            className={`mt-6 border-t pt-4 text-center ${dividerClass}`}
          >
            <p
              className={`text-[10px] leading-5 ${secondaryTextClass}`}
            >
              © {new Date().getFullYear()} Eternity Solutions Corp.
              All rights reserved.
            </p>
          </div>
        </section>
      </div>
    </main>
  )

  return content
}

export default Login